import {
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    makeStyles,
} from "@material-ui/core";
import * as React from "react";

import { FeedbackButtons, FeedbackTypeEnum } from ".";
import {
    Button,
    DialogCloseIconButton,
    RelodeTheme,
    Text,
    TextArea,
} from "../../";
import { useDeviceSize } from "../../components/hooks";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            dialogTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            },
            dialogContent: {
                paddingTop: 0,
            },
            buttons: {
                marginTop: theme.spacing(4),
            },
            upButton: {
                width: "80px",
                marginRight: theme.spacing(2),
            },
            downButton: {
                width: "80px",
            },
            error: {
                textAlign: "center",
            },
            dialogActions: {
                padding: theme.spacing(2),
            },
            field: {
                margin: theme.spacing(4, 0, 3, 0),
            },
        }),
    { name: "FeedbackModal" }
);

interface FeedbackModalProps {
    onCancel: () => void;
    onConfirm: (
        comment: string,
        vote: boolean,
        hasGivenFeedback?: boolean,
        jobId?: number,
        leadId?: number
    ) => void;
    selectedFeedbackType: FeedbackTypeEnum;
    comment: string;
    candidateFullName: string;
    hasGivenFeedback?: boolean;
    jobId?: number;
    leadId?: number;
    isLoading?: boolean;
}
export const FeedbackModal = (props: FeedbackModalProps) => {
    const classes = useStyles(props);
    const device = useDeviceSize();

    const {
        onCancel,
        onConfirm,
        comment,
        selectedFeedbackType: selectedFeedbackTypeProp,
        candidateFullName,
        hasGivenFeedback,
        jobId,
        leadId,
        isLoading,
    } = props;
    const [commentText, setCommentText] = React.useState<string>(comment);
    const [commentError, setCommentError] = React.useState<string>(undefined);
    const [voteError, setVoteError] = React.useState<string>(undefined);
    const [selectedFeedbackType, setSelectedFeedbackType] = React.useState<
        FeedbackTypeEnum
    >(selectedFeedbackTypeProp);

    const handleCommentChange = (event: React.ChangeEvent<any>) => {
        setCommentText(event.target.value);
        setCommentError(undefined);
    };
    const switchVoteType = (vote: FeedbackTypeEnum) => {
        setSelectedFeedbackType(vote);
        setVoteError(undefined);
    };

    const handleConfirm = () => {
        let isValid = true;
        if (!commentText || commentText.trim().length <= 0) {
            setCommentError("comment is required");
            isValid = false;
        }

        if (!selectedFeedbackType) {
            setVoteError("vote is required");
            isValid = false;
        }

        if (isValid && !isLoading) {
            onConfirm(
                commentText,
                selectedFeedbackType === FeedbackTypeEnum.Positive
                    ? true
                    : false,
                hasGivenFeedback,
                jobId,
                leadId
            );
        }
    };

    return (
        <Dialog
            open
            onClose={onCancel}
            fullWidth={device == "desktop" ? true : false}
            maxWidth={device == "desktop" ? "sm" : undefined}
            fullScreen={device === "mobile"}
            data-cy="feedbackDialog"
        >
            <DialogTitle
                disableTypography={true}
                className={classes.dialogTitle}
            >
                <Text variant="h1" isInline={true}>
                    your vote
                </Text>
                <DialogCloseIconButton onClick={onCancel} />
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Text variant="h4">{candidateFullName}</Text>
                <FeedbackButtons
                    onClick={switchVoteType}
                    selectedFeedbackType={selectedFeedbackType}
                    className={classes.buttons}
                />
                {!!voteError ? (
                    <Text
                        variant="body1"
                        color="danger"
                        className={classes.error}
                    >
                        {voteError}
                    </Text>
                ) : undefined}
                <TextArea
                    id="comment"
                    className={classes.field}
                    inputProps={{ maxLength: 300 }}
                    label="add a comment"
                    name="comment"
                    value={commentText}
                    showCharacterLimit
                    multiline={true}
                    rows={5}
                    onChange={handleCommentChange}
                    helperText={commentError}
                    error={!!commentError}
                    data-cy="feedbackComment"
                />
            </DialogContent>

            <DialogActions classes={{ root: classes.dialogActions }}>
                <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    fullWidth={device === "mobile"}
                    onClick={handleConfirm}
                    disabled={isLoading}
                    data-cy="feedbackSubmit"
                >
                    submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};
